import React from 'react';
import { CORE_DIFFERENTIATORS } from '../data/stitchData';
import logo from '../assets/logo.png';

export default function AboutUsSection({ onScheduleClick }) {
  return (
    <section
      id="about"
      className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 my-6 rounded-3xl bg-gradient-to-b from-surface-container-low/90 via-surface-container-lowest to-surface-container-low/50 dark:from-clinical-slate-800/90 dark:via-clinical-slate-900 dark:to-clinical-slate-800/60 backdrop-blur-xl border border-glass-border-light dark:border-gray-800 shadow-sm transition-colors duration-300"
    >
      <div className="relative z-10 flex flex-col space-y-12">
        {/* Narrative Section Header */}
        <div className="flex flex-col items-center text-center space-y-4 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-container-low/80 dark:bg-clinical-slate-800/80 backdrop-blur-xl border border-glass-border-light dark:border-gray-700 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-accent-teal animate-pulse"></span>
            <span className="text-[11px] uppercase tracking-wider text-primary dark:text-primary-fixed font-bold">
              About NFBG Scorpio Advisory
            </span>
          </div>

          <h2 className="text-2xl sm:text-4xl lg:text-4xl text-gray-900 dark:text-white font-bold tracking-tight">
            Bridging Clinical Governance &amp; Executive Leadership in High-Stakes Healthcare
          </h2>

          <p className="text-base sm:text-lg text-gray-600 dark:text-slate-300 max-w-3xl leading-relaxed">
            NFBG Scorpio is a specialized healthcare advisory and alternative dispute resolution (ADR) practice.
            We partner with hospital health systems, physician group practices, academic medical centers, and executive
            boards to transform deep-seated operational friction, medical staff impasses, and organizational transitions
            into lasting clinical alignment and cultural resilience.
          </p>
        </div>

        {/* 3 Core Differentiator Cards */}
        <div className="p-6 sm:p-8 rounded-[32px] bg-white/90 dark:bg-clinical-slate-900/90 backdrop-blur-xl border border-glass-border-light dark:border-gray-800 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {CORE_DIFFERENTIATORS.map((diff, dIdx) => (
              <div key={dIdx} className="flex items-start gap-3.5">
                <div
                  className={`w-11 h-11 rounded-xl ${diff.colorClass} dark:bg-clinical-slate-800 flex items-center justify-center shrink-0 mt-0.5 shadow-xs`}
                >
                  <span className="material-symbols-outlined text-[24px]">{diff.icon}</span>
                </div>
                <div className="space-y-1">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white leading-snug">{diff.title}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-300 leading-relaxed">{diff.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* "NFBG Scorpio Trusted" Feature Showcase */}
        <div className="p-8 sm:p-12 rounded-[32px] bg-white/95 dark:bg-clinical-slate-900/95 backdrop-blur-xl border border-gray-200/90 dark:border-gray-800 shadow-md">
          {/* Top Row: Brand & Narrative */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Brand Identity: Borderless Logo + Prestigious Spacious Title */}
            <div className="lg:col-span-5 flex items-center gap-6 sm:gap-7">
              {/* Borderless Emblem with natural transparent presence */}
              <img
                src={logo}
                alt="NFBG Scorpio Emblem"
                className="h-24 sm:h-28 lg:h-32 w-auto object-contain shrink-0 drop-shadow-sm transition-transform duration-300 hover:scale-105"
              />

              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-accent-teal/10 dark:bg-accent-teal/20 text-accent-teal text-xs font-bold uppercase tracking-widest border border-accent-teal/20">
                  <span className="material-symbols-outlined text-[15px]">verified</span>
                  <span>Institutional Credibility</span>
                </div>
                
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 dark:text-white tracking-normal leading-tight">
                  NFBG Scorpio <span className="text-primary font-bold">Trusted</span>
                </h3>

                <p className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 font-medium tracking-wide leading-relaxed">
                  Executive Dispute Resolution &amp; Enterprise Healthcare Governance
                </p>
              </div>
            </div>

            {/* Right Side: Spacious Narrative & Request Briefing Button */}
            <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
              <p className="text-base sm:text-lg text-gray-700 dark:text-slate-200 leading-relaxed font-normal">
                Trusted by acute care hospital networks, medical executive committees, and physician group leadership
                to resolve sensitive clinical friction, preserve crucial contracts, and build durable executive alignment
                without public exposure or regulatory disruption.
              </p>

              <div>
                <button
                  type="button"
                  onClick={onScheduleClick}
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-primary hover:bg-primary-container text-white text-sm font-bold shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                >
                  <span>Request Executive Briefing</span>
                  <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
              </div>
            </div>

          </div>

          {/* Bottom Divider & 4-Column Credential Badges */}
          <div className="mt-8 pt-8 border-t border-gray-200/80 dark:border-gray-800">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50/70 dark:bg-clinical-slate-800/50 border border-gray-100 dark:border-gray-700/50">
                <span className="material-symbols-outlined text-primary text-[22px] shrink-0">gavel</span>
                <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 leading-snug">
                  Statutory Privilege Protection
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50/70 dark:bg-clinical-slate-800/50 border border-gray-100 dark:border-gray-700/50">
                <span className="material-symbols-outlined text-accent-teal text-[22px] shrink-0">award_star</span>
                <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 leading-snug">
                  ADR Certified Mediators
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50/70 dark:bg-clinical-slate-800/50 border border-gray-100 dark:border-gray-700/50">
                <span className="material-symbols-outlined text-secondary text-[22px] shrink-0">verified</span>
                <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 leading-snug">
                  Prosci® ADKAR® Certified
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50/70 dark:bg-clinical-slate-800/50 border border-gray-100 dark:border-gray-700/50">
                <span className="material-symbols-outlined text-primary text-[22px] shrink-0">local_hospital</span>
                <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 leading-snug">
                  High-Reliability Aligned
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
